import type { Money } from '../api/types';
import { currentLocale } from '../i18n';

/** Vietnam time: UTC+7, no daylight saving. */
const VN_OFFSET_MS = 7 * 3_600_000;

/**
 * Timestamps are stored in UTC and shown in Vietnam time (owner 2026-10-07: "chỉnh giờ VN"),
 * marked GMT+7 so they are never mistaken for the UTC of EPP answers and logs.
 */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${new Date(d.getTime() + VN_OFFSET_MS).toISOString().slice(0, 16).replace('T', ' ')} (GMT+7)`;
}

/** A log line's timestamp to the millisecond, in Vietnam time (the log store keeps UTC). */
export function formatDateTimeMs(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${new Date(d.getTime() + VN_OFFSET_MS).toISOString().slice(0, 23).replace('T', ' ')} (GMT+7)`;
}

/** The Vietnam calendar day of a timestamp. */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : new Date(d.getTime() + VN_OFFSET_MS).toISOString().slice(0, 10);
}

const relativeFormats = new Map<string, Intl.RelativeTimeFormat>();
function relative(): Intl.RelativeTimeFormat {
  const locale = currentLocale();
  let f = relativeFormats.get(locale);
  if (!f) {
    f = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
    relativeFormats.set(locale, f);
  }
  return f;
}

export function formatRelative(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return '';
  const diffMs = new Date(iso).getTime() - now;
  if (Number.isNaN(diffMs)) return '';
  const minutes = Math.round(diffMs / 60_000);
  if (Math.abs(minutes) < 60) return relative().format(minutes, 'minute');
  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 48) return relative().format(hours, 'hour');
  const days = Math.round(hours / 24);
  if (Math.abs(days) < 90) return relative().format(days, 'day');
  return relative().format(Math.round(days / 30), 'month');
}

/** Seconds from `fromIso` to `toIso`, or to now when `toIso` is null. */
export function elapsedSeconds(fromIso: string, toIso: string | null = null, now: number = Date.now()): number {
  const end = toIso ? new Date(toIso).getTime() : now;
  return Math.max(0, (end - new Date(fromIso).getTime()) / 1000);
}

export function daysUntil(iso: string, now: number = Date.now()): number {
  return Math.floor((new Date(iso).getTime() - now) / 86_400_000);
}

export function formatPercent(ratio: number | null | undefined, fractionDigits = 1): string {
  if (ratio === null || ratio === undefined || Number.isNaN(ratio)) return '—';
  return `${(ratio * 100).toFixed(fractionDigits)}%`;
}

export function formatCount(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat(currentLocale()).format(value);
}

export function formatDuration(totalSeconds: number | null | undefined): string {
  if (totalSeconds === null || totalSeconds === undefined) return '—';
  const s = Math.max(0, Math.round(totalSeconds));
  const d = Math.floor(s / 86_400);
  const h = Math.floor((s % 86_400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

/** ISO 4217 minor-unit digits, used only to pad the display (never to round). */
const CURRENCY_DIGITS: Record<string, number> = { VND: 0, JPY: 0, KRW: 0, USD: 2, EUR: 2 };

export interface ParsedDecimal {
  negative: boolean;
  whole: bigint;
  /** Fractional digits exactly as sent. */
  fraction: string;
}

/** Parses the backend's decimal-string amounts exactly (no floating point). */
export function parseDecimal(amount: string): ParsedDecimal | null {
  const m = /^([+-])?(\d+)(?:\.(\d+))?$/.exec(amount.trim());
  if (!m?.[2]) return null;
  return { negative: m[1] === '-', whole: BigInt(m[2]), fraction: m[3] ?? '' };
}

/** `true` for "0", "0.00", "-0"; `false` for any other number; `null` if unparseable. */
export function isZeroAmount(amount: string): boolean | null {
  const d = parseDecimal(amount);
  return d ? d.whole === 0n && !/[1-9]/.test(d.fraction) : null;
}

/**
 * "150000.00" VND -> "150,000 VND"; "12.5" USD -> "12.50 USD". Significant
 * fractional digits are always kept, so a sub-unit ledger residue stays visible.
 */
export function formatMoney(money: Money): string {
  const d = parseDecimal(money.amount);
  if (!d) return `${money.amount} ${money.currency} (unparseable)`;
  const digits = CURRENCY_DIGITS[money.currency] ?? 2;
  const fraction = d.fraction.replace(/0+$/, '').padEnd(digits, '0');
  const sign = d.negative && isZeroAmount(money.amount) === false ? '−' : '';
  const locale = currentLocale();
  const decimal = new Intl.NumberFormat(locale).formatToParts(1.5).find((p) => p.type === 'decimal')?.value ?? '.';
  return `${sign}${new Intl.NumberFormat(locale).format(d.whole)}${fraction ? `${decimal}${fraction}` : ''} ${money.currency}`;
}
